import React, { useEffect, useState, useMemo } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import { Select, Input, Spin, Empty, Tooltip, Modal, Table, Button, Form, DatePicker, InputNumber, Popconfirm, message, Checkbox, Progress } from 'antd';
import * as Icons from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { customThemes } from '../../../../themeConfig';
import { saveAs } from 'file-saver';

interface Product {
  product_id: number;
  product_name: string;
}

interface User {
  user_id: number;
  user_name: string;
  user_email: string;
}

interface PIItem {
  pid_id: number;
  pid_pi_id: number;
  pid_product_id: number;
  pid_qty: number;
}

interface PICard {
  pi_id: number;
  pi_order_no: string;
  pi_date: string;
  pi_lead_time: number;
  pi_created_by: number;
  ledger_company_name: string;
  ledger_first_name: string;
  ledger_last_name: string;
  items: PIItem[];
}

interface FlatWorkOrder {
  pid_id: number;
  wo_id: number;
  pi_id: number;
  pi_order_no: string;
  pi_date: string;
  pi_lead_time: number;
  pi_created_by: number;
  ledger_company_name: string;
  ledger_first_name: string;
  ledger_last_name: string;
  product_id: number;
  product_name: string;
  qty: number;
  employee_fname?: string;
  employee_lname?: string;
  wo_status?: string;
  daysLeft: number;
  statusText: string;
  borderColor: string;
  glowColor: string;
  badgeBg: string;
  total_complete?: number;
  wo_due_date?: string;
  wo_number?: string;
  wo_employee_id?: number;
  isMachinery?: boolean;
  wo_done_on_time?: 'Y' | 'N' | null;
  isCompleted?: boolean;
  progressPercent?: number;
  completedComponentsCount?: number;
  totalComponentsCount?: number;
}

const FALLBACK_DEPARTMENTS = [
  { key: 'timber', label: 'Timber', color: '#8b5cf6', id: 9 },
  { key: 'wood_components', label: 'Wood Comp.', color: '#3b82f6', id: 9 },
  { key: 'machinery', label: 'Machinery', color: '#06b6d4', id: 10 },
  { key: 'assembly', label: 'Assembly', color: '#10b981', id: 11 },
  { key: 'sanding', label: 'Sanding', color: '#ec4899', id: 12 },
  { key: 'finishing', label: 'Finishing', color: '#ff7875', id: 13 },
  { key: 'packed', label: 'Packed', color: '#52c41a', id: 7 },
];

const TrackWorkOrder: React.FC = () => {
  const [departmentsList, setDepartmentsList] = useState<any[]>(FALLBACK_DEPARTMENTS);
  const DEPARTMENTS = departmentsList;

  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [boms, setBoms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [woDetailsMap, setWoDetailsMap] = useState<Record<number, any[]>>({});
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  // Filters
  const [selectedDept, setSelectedDept] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedPI, setSelectedPI] = useState<string | null>(() => {
    return searchParams.get('pi') || searchParams.get('pi_order_no') || null;
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const urlPI = searchParams.get('pi') || searchParams.get('pi_order_no');
    if (urlPI && urlPI !== selectedPI) {
      setSelectedPI(urlPI);
    } else if (!urlPI && selectedPI && searchParams.has('pi')) {
      setSelectedPI(null);
    }
  }, [searchParams]);

  const handlePIChange = (val: string | null) => {
    setSelectedPI(val);
    const newParams = new URLSearchParams(searchParams);
    if (val) {
      newParams.set('pi', val);
    } else {
      newParams.delete('pi');
      newParams.delete('pi_order_no');
    }
    setSearchParams(newParams, { replace: true });
  };

  const handleClearFilters = () => {
    setSelectedDept(null);
    setSelectedUser(null);
    setSelectedPI(null);
    setSearchQuery('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('pi');
    newParams.delete('pi_order_no');
    setSearchParams(newParams, { replace: true });
  };

  // Daily Completion Details Modal State
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedWO, setSelectedWO] = useState<FlatWorkOrder | null>(null);
  const [dailyDetails, setDailyDetails] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Form Modal State (Add/Edit)
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingDetail, setEditingDetail] = useState<any | null>(null);
  const [form] = Form.useForm();

  // Download PDF Modal State
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [selectedWOIds, setSelectedWOIds] = useState<number[]>([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const isMachineryWO = useMemo(() => {
    if (!selectedWO || !employees.length) return false;
    const woEmployee = employees.find(e => e.employee_id === selectedWO.wo_employee_id);
    if (!woEmployee || !woEmployee.employee_department) return false;

    const machineryDept = departmentsList.find(d => String(d.label).toLowerCase().includes("machinery"));
    if (!machineryDept) return false;

    const deptIds = String(woEmployee.employee_department).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
    return deptIds.includes(Number(machineryDept.id));
  }, [selectedWO, employees, departmentsList]);

  const matchedBom = useMemo(() => {
    if (!selectedWO || !boms.length) return null;
    return boms.find(b => Number(b.bom_product_id) === Number(selectedWO.product_id));
  }, [selectedWO, boms]);

  const bomComponents = useMemo(() => {
    if (!matchedBom || !Array.isArray(matchedBom.components)) return [];
    return matchedBom.components;
  }, [matchedBom]);

  const showComponentDropdown = isMachineryWO && bomComponents.length > 0;

  const fetchDailyDetails = async (woId: number) => {
    try {
      setLoadingDetails(true);
      const res = await apiRequest<any[]>({
        method: 'get',
        url: `/work-order-details/${woId}`
      });
      const list = res || [];
      setDailyDetails(list);
      setWoDetailsMap(prev => ({
        ...prev,
        [woId]: list
      }));
    } catch (err) {
      console.error(err);
      message.error('Failed to load daily details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const openDetailsModal = (wo: FlatWorkOrder) => {
    setSelectedWO(wo);
    setDetailsModalVisible(true);
    fetchDailyDetails(wo.wo_id);
  };

  const handleOpenAdd = () => {
    setEditingDetail(null);
    form.resetFields();
    form.setFieldsValue({
      wod_created_on: dayjs(),
      wod_qty: undefined,
      wod_remarks: ''
    });
    setFormModalVisible(true);
  };

  const handleOpenEdit = (record: any) => {
    setEditingDetail(record);
    form.resetFields();

    const match = String(record.wod_remarks || '').match(/^\[(.*?)\](?: (.*))?$/);
    const componentName = match ? match[1] : undefined;
    const remarks = match ? (match[2] || '') : record.wod_remarks;

    form.setFieldsValue({
      wod_created_on: dayjs(record.wod_created_on),
      wod_qty: record.wod_qty,
      wod_remarks: remarks,
      wod_component: componentName
    });
    setFormModalVisible(true);
  };

  const handleSaveDetail = async (values: any) => {
    if (!selectedWO) return;
    try {
      let finalRemarks = values.wod_remarks || '';
      if (values.wod_component) {
        finalRemarks = `[${values.wod_component}]` + (values.wod_remarks ? ` ${values.wod_remarks}` : '');
      }

      const payload = {
        wod_id: editingDetail?.wod_id || null,
        wod_wo_id: selectedWO.wo_id,
        wod_qty: Number(values.wod_qty),
        wod_remarks: finalRemarks,
        wod_created_on: editingDetail
          ? (values.wod_created_on ? values.wod_created_on.format('YYYY-MM-DD HH:mm:ss') : dayjs().format('YYYY-MM-DD HH:mm:ss'))
          : dayjs().format('YYYY-MM-DD HH:mm:ss')
      };

      // Validation check: positive complete pieces cannot exceed target
      if (payload.wod_qty > 0) {
        if (values.wod_component) {
          const currentCompletedSumForComponent = dailyDetails
            .filter(d => {
              if (d.wod_id === editingDetail?.wod_id) return false;
              if (Number(d.wod_qty) <= 0) return false;
              const dMatch = String(d.wod_remarks || '').match(/^\[(.*?)\]/);
              return dMatch && dMatch[1] === values.wod_component;
            })
            .reduce((sum, d) => sum + Number(d.wod_qty), 0);

          if (currentCompletedSumForComponent + payload.wod_qty > selectedWO.qty) {
            message.error(`Cannot complete more than the work order target quantity of ${selectedWO.qty} pcs for component "${values.wod_component}". Currently completed: ${currentCompletedSumForComponent} pcs.`);
            return;
          }
        } else {
          const currentCompletedSum = dailyDetails
            .filter(d => d.wod_id !== editingDetail?.wod_id && Number(d.wod_qty) > 0)
            .reduce((sum, d) => sum + Number(d.wod_qty), 0);

          if (currentCompletedSum + payload.wod_qty > selectedWO.qty) {
            message.error(`Cannot complete more than the work order target quantity of ${selectedWO.qty} pcs. Currently completed: ${currentCompletedSum} pcs.`);
            return;
          }
        }
      }

      await apiRequest({
        method: 'post',
        url: '/work-order-details',
        data: payload
      });

      message.success(editingDetail ? 'Daily detail updated successfully' : 'Daily detail added successfully');
      setFormModalVisible(false);
      setEditingDetail(null);
      form.resetFields();

      fetchDailyDetails(selectedWO.wo_id);
      fetchData(); // Refresh main lists
    } catch (err) {
      console.error(err);
      message.error('Failed to save daily detail');
    }
  };

  const handleDeleteDetail = async (wodId: number) => {
    if (!selectedWO) return;
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-work-order-details/${wodId}`
      });
      message.success('Daily detail deleted successfully');
      fetchDailyDetails(selectedWO.wo_id);
      fetchData();
    } catch (err) {
      console.error(err);
      message.error('Failed to delete daily detail');
    }
  };

  const handleDownloadSelectedPDF = async () => {
    if (selectedWOIds.length === 0) {
      message.warning('Please select at least one work order');
      return;
    }

    try {
      setDownloadingPdf(true);
      const woIdsStr = selectedWOIds.join(',');
      const blob = await apiRequest<Blob>({
        method: 'get',
        url: `/download-work-orders?wo_ids=${woIdsStr}`,
        config: { responseType: 'blob' }
      });
      saveAs(blob, `Work-Orders-${dayjs().format('YYYY-MM-DD-HHmmss')}.pdf`);
      message.success('PDF downloaded successfully');
      setDownloadModalVisible(false);
      setSelectedWOIds([]);
    } catch (err) {
      console.error(err);
      message.error('Failed to download work orders PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Context styles
  const context = useOutletContext<any>() || {};
  const darkMode = context.darkMode || false;
  const baseTheme = darkMode ? customThemes.dark : customThemes.light;
  const themeStyles = { ...baseTheme, ...context.themeStyles };

  const customLayoutBg = darkMode ? '#0f172a' : '#f8fafc'; // Slate-900 vs Slate-50
  const customCardBg = darkMode ? '#1e293b' : '#ffffff';   // Slate-800 vs Pure White
  const customCardBorder = darkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(226, 232, 240, 0.8)'; // Slate-700 vs Slate-200
  const customTextMain = darkMode ? '#f8fafc' : '#0f172a';  // Slate-50 vs Slate-900
  const customTextSub = darkMode ? '#94a3b8' : '#64748b';   // Slate-400 vs Slate-500

  // Fetch Work Orders, Users, Departments, Employees, and BOMs
  const fetchData = async () => {
    try {
      setLoading(true);
      const [woData, userData, deptData, empData, bomData] = await Promise.all([
        apiRequest<any[]>({ method: 'get', url: '/get-work-orders' }),
        apiRequest<any>({ method: 'get', url: '/users' }),
        apiRequest<any[]>({ method: 'get', url: '/departments' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/employees?status=1' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/get-bom' }).catch(() => [])
      ]);

      setWorkOrders(woData || []);
      setUsers(userData || []);
      setEmployees(empData || []);
      setBoms(bomData || []);

      const colorsPalette = ['#8b5cf6', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'];
      const activeDepts = (deptData || []).filter((d: any) => d.department_status === 'Y' || Number(d.department_status) === 1 || String(d.department_status) === '1');

      const ALLOWED_DEPARTMENTS = ["Machinery", "Assembly", "Sanding", "Finishing", "Packing"];
      const mappedDepts = activeDepts
        .map((dept: any, index: number) => ({
          key: String(dept.department_name).toLowerCase().replace(/\s+/g, '_'),
          label: dept.department_name,
          color: colorsPalette[index % colorsPalette.length],
          id: dept.department_id
        }))
        .filter((d: any) =>
          ALLOWED_DEPARTMENTS.some(allowed => allowed.toLowerCase() === d.label.toLowerCase())
        )
        .sort((a: any, b: any) => {
          const indexA = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === a.label.toLowerCase());
          const indexB = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === b.label.toLowerCase());
          return indexA - indexB;
        });

      let currentDepts = departmentsList;
      if (mappedDepts.length > 0) {
        setDepartmentsList(mappedDepts);
        currentDepts = mappedDepts;
      } else {
        const filteredFallbacks = FALLBACK_DEPARTMENTS
          .filter((d: any) =>
            ALLOWED_DEPARTMENTS.some(allowed => allowed.toLowerCase() === d.label.toLowerCase())
          )
          .sort((a: any, b: any) => {
            const indexA = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === a.label.toLowerCase());
            const indexB = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === b.label.toLowerCase());
            return indexA - indexB;
          });
        setDepartmentsList(filteredFallbacks);
        currentDepts = filteredFallbacks;
      }

      const machineryDept = currentDepts.find((d: any) => String(d.label).toLowerCase().includes("machinery"));

      const machineryWOs = (woData || []).filter((wo: any) => {
        const emp = (empData || []).find((e: any) => e.employee_id === wo.wo_employee_id);
        const empInMachinery = !!(emp && emp.employee_department && machineryDept &&
          String(emp.employee_department).split(',').map((id: any) => parseInt(String(id).trim(), 10)).includes(Number(machineryDept.id)));
        const woDeptInMachinery = !!(wo.wo_department_name && String(wo.wo_department_name).toLowerCase().includes('machinery'));
        return empInMachinery || woDeptInMachinery;
      });

      const detailsMap: Record<number, any[]> = {};
      if (machineryWOs.length > 0) {
        const detailsResults = await Promise.all(
          machineryWOs.map((wo: any) =>
            apiRequest<any[]>({ method: 'get', url: `/work-order-details/${wo.wo_id}` })
              .then(res => ({ wo_id: wo.wo_id, details: res || [] }))
              .catch(() => ({ wo_id: wo.wo_id, details: [] }))
          )
        );
        detailsResults.forEach(({ wo_id, details }) => {
          detailsMap[wo_id] = details;
        });
      }

      setWoDetailsMap(detailsMap);
    } catch (error) {
      console.error('Fetch track data error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Construct Flat Work Orders Array
  const flatWorkOrders = useMemo(() => {
    return workOrders.map((wo) => {
      const deadline = wo.wo_due_date
        ? dayjs(wo.wo_due_date)
        : dayjs(wo.pi_date).add(wo.pi_lead_time, 'day');
      const today = dayjs().startOf('day');
      const daysLeft = deadline.startOf('day').diff(today, 'day');

      const emp = employees.find(e => e.employee_id === wo.wo_employee_id);
      const machineryDept = departmentsList.find(d => String(d.label).toLowerCase().includes("machinery"));
      const isMachinery = !!(
        (emp && emp.employee_department && machineryDept &&
          String(emp.employee_department).split(',').map(id => parseInt(id.trim(), 10)).includes(Number(machineryDept.id))) ||
        (wo.wo_department_name && String(wo.wo_department_name).toLowerCase().includes('machinery'))
      );

      const matchedBom = boms.find(b => Number(b.bom_product_id) === Number(wo.product_id));
      const bomComponents = matchedBom && Array.isArray(matchedBom.components) ? matchedBom.components : [];

      const targetQty = Number(wo.wo_qty) || 1;
      let isCompleted = false;
      let progressPercent = 0;
      let completedComponentsCount = 0;
      let totalComponentsCount = 0;

      if (isMachinery && bomComponents.length > 0) {
        totalComponentsCount = bomComponents.length;
        const details = woDetailsMap[wo.wo_id] || [];
        const totalCompTarget = totalComponentsCount * targetQty;
        let totalCompCompleted = 0;
        let allDone = true;

        bomComponents.forEach((c: any) => {
          const compName = c.component_name;
          const compCompleted = details
            .filter((d: any) => {
              if (Number(d.wod_qty) <= 0) return false;
              const match = String(d.wod_remarks || '').match(/^\[(.*?)\]/);
              return match && match[1] === compName;
            })
            .reduce((sum: number, d: any) => sum + Number(d.wod_qty), 0);

          totalCompCompleted += Math.min(compCompleted, targetQty);
          if (compCompleted >= targetQty) {
            completedComponentsCount++;
          } else {
            allDone = false;
          }
        });

        isCompleted = allDone;
        progressPercent = totalCompTarget > 0 ? (totalCompCompleted / totalCompTarget) * 100 : 0;
      } else {
        const completedQty = Number(wo.total_complete || 0);
        isCompleted = completedQty >= targetQty;
        progressPercent = Math.min((completedQty / targetQty) * 100, 100);
      }

      let borderColor = '#52c41a'; // Green (On Time)
      let glowColor = 'rgba(82, 196, 26, 0.18)';
      let badgeBg = 'rgba(82, 196, 26, 0.08)';
      let statusText = '';

      if (isCompleted) {
        if (wo.wo_done_on_time === 'Y') {
          borderColor = '#52c41a';
          glowColor = 'rgba(82, 196, 26, 0.18)';
          badgeBg = 'rgba(82, 196, 26, 0.08)';
          statusText = 'Completed (On Time)';
        } else if (wo.wo_done_on_time === 'N') {
          borderColor = '#ff4d4f';
          glowColor = 'rgba(255, 77, 79, 0.18)';
          badgeBg = 'rgba(255, 77, 79, 0.08)';
          statusText = 'Completed (Late)';
        } else {
          borderColor = '#52c41a';
          glowColor = 'rgba(82, 196, 26, 0.18)';
          badgeBg = 'rgba(82, 196, 26, 0.08)';
          statusText = 'Completed';
        }
      } else {
        statusText = `${daysLeft} days left`;
        if (daysLeft < 0) {
          borderColor = '#ff4d4f'; // Red (Overdue)
          glowColor = 'rgba(255, 77, 79, 0.18)';
          badgeBg = 'rgba(255, 77, 79, 0.08)';
          statusText = `${Math.abs(daysLeft)}d overdue`;
        } else if (daysLeft <= 3) {
          borderColor = '#faad14'; // Yellow (Due Soon)
          glowColor = 'rgba(250, 173, 20, 0.18)';
          badgeBg = 'rgba(250, 173, 20, 0.08)';
          statusText = daysLeft === 0 ? 'Due today' : `${daysLeft}d left`;
        }
      }

      return {
        pid_id: wo.wo_pid_detail_id,
        wo_id: wo.wo_id,
        pi_id: wo.wo_pi_id,
        pi_order_no: String(wo.pi_order_no),
        pi_date: wo.pi_date,
        pi_lead_time: wo.pi_lead_time,
        pi_created_by: wo.pi_created_by,
        ledger_company_name: wo.ledger_company_name,
        ledger_first_name: wo.ledger_first_name,
        ledger_last_name: wo.ledger_last_name,
        product_id: wo.product_id,
        product_name: wo.product_name,
        qty: Number(wo.wo_qty),
        employee_fname: wo.employee_fname,
        employee_lname: wo.employee_lname,
        wo_status: wo.wo_status,
        wo_number: wo.wo_number,
        wo_employee_id: wo.wo_employee_id,
        daysLeft,
        statusText,
        borderColor,
        glowColor,
        badgeBg,
        total_complete: Number(wo.total_complete || 0),
        wo_due_date: wo.wo_due_date,
        wo_done_on_time: wo.wo_done_on_time,
        isMachinery,
        isCompleted,
        progressPercent,
        completedComponentsCount,
        totalComponentsCount,
      };
    });
  }, [workOrders, employees, departmentsList, boms, woDetailsMap]);

  // Keep selectedWO in sync with updated flatWorkOrders
  useEffect(() => {
    if (selectedWO) {
      const updated = flatWorkOrders.find(w => w.wo_id === selectedWO.wo_id);
      if (updated && (
        updated.isCompleted !== selectedWO.isCompleted ||
        updated.progressPercent !== selectedWO.progressPercent ||
        updated.statusText !== selectedWO.statusText ||
        updated.completedComponentsCount !== selectedWO.completedComponentsCount
      )) {
        setSelectedWO(updated);
      }
    }
  }, [flatWorkOrders]);

  // Extract Unique PI Numbers for Filter Dropdown
  const piOptions = useMemo(() => {
    const set = new Set<string>();
    flatWorkOrders.forEach((wo) => {
      if (wo.pi_order_no) set.add(wo.pi_order_no);
    });
    return Array.from(set);
  }, [flatWorkOrders]);

  // Apply Advanced Filtering (Department, User, PI, Query Search)
  const filteredWorkOrders = useMemo(() => {
    return flatWorkOrders.filter((wo) => {
      // 1. User Filter
      if (selectedUser !== null && selectedUser !== undefined && wo.pi_created_by !== selectedUser) {
        return false;
      }

      // 2. PI Filter
      if (selectedPI && wo.pi_order_no !== selectedPI) {
        return false;
      }

      // 3. Department Filter (based on assigned employee's department)
      if (selectedDept) {
        const emp = employees.find(e => e.employee_id === wo.wo_employee_id);
        if (!emp || !emp.employee_department) return false;

        const deptObj = departmentsList.find(d => d.key === selectedDept);
        if (!deptObj) return false;

        const deptIds = String(emp.employee_department).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
        if (!deptIds.includes(Number(deptObj.id))) {
          return false;
        }
      }

      // 4. Search Query Filter (Work Order ID, PI order #, Product Name, Customer, Employee)
      const customer = String(`${wo.ledger_company_name || ''} ${wo.ledger_first_name || ''} ${wo.ledger_last_name || ''}`).toLowerCase();
      const product = String(wo.product_name || '').toLowerCase();
      const piOrder = String(wo.pi_order_no || '').toLowerCase();
      const employee = String(`${wo.employee_fname || ''} ${wo.employee_lname || ''}`).toLowerCase();
      const woId = `wo-${wo.wo_id}`;
      const query = searchQuery.toLowerCase();

      return (
        customer.includes(query) ||
        product.includes(query) ||
        piOrder.includes(query) ||
        employee.includes(query) ||
        woId.includes(query) ||
        String(wo.wo_id).includes(query)
      );
    });
  }, [flatWorkOrders, selectedUser, selectedPI, selectedDept, searchQuery, departmentsList, employees]);

  // Group Work Orders by assigned employee's department
  const groupedWorkOrders = useMemo(() => {
    const groups: Record<string, any[]> = {};

    // Initialize groups for each active department key
    DEPARTMENTS.forEach(dept => {
      groups[dept.key] = [];
    });
    // Add a group for unassigned work orders
    groups['unassigned'] = [];

    filteredWorkOrders.forEach(wo => {
      const emp = employees.find(e => e.employee_id === wo.wo_employee_id);
      if (!emp || !emp.employee_department) {
        groups['unassigned'].push(wo);
        return;
      }

      const empDeptIds = String(emp.employee_department).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
      const matchedDept = DEPARTMENTS.find(d => empDeptIds.includes(Number(d.id)));
      if (matchedDept) {
        groups[matchedDept.key].push(wo);
      } else {
        groups['unassigned'].push(wo);
      }
    });

    return groups;
  }, [filteredWorkOrders, DEPARTMENTS, employees]);

  /* ================= INJECT DYNAMIC HOVER STYLES ================= */
  useEffect(() => {
    const styleId = "track-work-order-cards-style";
    let styleTag = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = `
      @keyframes cardSlideIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .track-bento-card {
        background: ${customCardBg} !important;
        border: 1px solid ${customCardBorder} !important;
        border-radius: 12px !important;
        padding: 22px 24px !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: space-between !important;
        min-height: 190px !important;
        position: relative !important;
        overflow: hidden !important;
        transition: box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease !important;
        cursor: pointer !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02), 0 1px 2px rgba(0, 0, 0, 0.01) !important;
      }
      .track-bento-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 4px;
        background-color: var(--accent-color);
        transition: width 0.2s ease-out;
        z-index: 2;
        pointer-events: none;
      }
      /* Sheen sweep overlay */
      .track-bento-card::after {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: linear-gradient(
          45deg,
          transparent 45%,
          ${darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.25)'} 50%,
          transparent 55%
        );
        transform: rotate(-45deg) translate(-75%, -75%);
        transition: transform 0.6s ease-out;
        pointer-events: none;
        z-index: 1;
      }
      .track-bento-card:hover {
        border-color: var(--accent-color) !important;
        box-shadow: 0 12px 28px -4px ${darkMode ? 'rgba(0, 0, 0, 0.55)' : 'rgba(148, 163, 184, 0.22)'}, 0 0 16px var(--accent-glow) !important;
      }
      .track-bento-card:hover::before {
        width: 6px;
      }
      .track-bento-card:hover::after {
        transform: rotate(-45deg) translate(75%, 75%);
      }
      .track-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: var(--badge-bg) !important;
        color: var(--accent-color) !important;
        border: 1px solid var(--accent-color) !important;
        border-radius: 20px;
        padding: 4px 10px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }
      
      /* Progress bar text styling */
      .ant-progress-text {
        color: ${customTextMain} !important;
        font-weight: 700 !important;
        font-size: 12px !important;
      }
      
      .ant-select-selector {
        background: ${customCardBg} !important;
        border-color: ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)'} !important;
        color: ${customTextMain} !important;
        border-radius: 6px !important;
      }
      .ant-select-selection-item {
        color: ${customTextMain} !important;
        font-size: 13px !important;
      }
      .ant-select-selection-placeholder {
        color: ${customTextSub} !important;
        font-size: 13px !important;
      }
      .ant-select-arrow {
        color: ${customTextSub} !important;
      }
      .ant-select-dropdown {
        background-color: ${customCardBg} !important;
        border: 1px solid ${customCardBorder} !important;
        padding: 4px !important;
        border-radius: 8px !important;
      }
      .ant-select-item-option {
        color: ${customTextMain} !important;
        border-radius: 4px !important;
        transition: background-color 0.2s ease !important;
      }
      .ant-select-item-option-active {
        background-color: ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'} !important;
        color: ${customTextMain} !important;
      }
      .ant-select-item-option-selected {
        background-color: ${themeStyles.colorPrimary || '#1890ff'} !important;
        color: #ffffff !important;
      }
    `;
  }, [customCardBg, customCardBorder, customTextMain, themeStyles, darkMode]);

  const renderWorkOrderCard = (wo: FlatWorkOrder, index: number, isCompleted: boolean) => {
    const progressPercent = wo.progressPercent !== undefined
      ? wo.progressPercent
      : Math.min(((wo.total_complete || 0) / (wo.qty || 1)) * 100, 100);

    const cardAccentColor = isCompleted
      ? (wo.wo_done_on_time === 'N' ? '#ff4d4f' : '#52c41a')
      : '#1890ff';
    const cardStageLabel = isCompleted
      ? (wo.wo_done_on_time === 'Y'
        ? 'Completed (On Time)'
        : (wo.wo_done_on_time === 'N' ? 'Completed (Late)' : 'Completed')
      )
      : 'Active';
    const accentGlow = isCompleted
      ? (wo.wo_done_on_time === 'N' ? 'rgba(255, 77, 79, 0.18)' : 'rgba(82, 196, 26, 0.18)')
      : 'rgba(24, 144, 255, 0.18)';

    return (
      <div
        key={wo.wo_id}
        className="track-bento-card"
        onClick={() => openDetailsModal(wo)}
        style={{
          '--accent-color': cardAccentColor,
          '--accent-glow': accentGlow,
          '--badge-bg': `${cardAccentColor}15`,
          animationDelay: `${index * 40}ms`
        } as React.CSSProperties}
      >
        {/* Section 1: Work Order ID & Status Badge */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: '800', margin: 0, color: customTextMain, letterSpacing: '-0.02em' }}>
              {wo.wo_number || `WO-${wo.wo_id}`}
            </h3>
            <span style={{ fontSize: '11px', fontWeight: '700', color: customTextSub, letterSpacing: '0.05em' }}>
              PI ORDER #{wo.pi_order_no}
            </span>
          </div>

          <div className="track-badge">
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: wo.borderColor, display: 'inline-block' }} />
            {wo.statusText}
          </div>
        </div>

        {/* Section 2: Product Name & Quantity Details */}
        <div style={{ margin: '14px 0 10px 0', width: '100%' }}>
          <h4 style={{ fontSize: '15px', fontWeight: '700', margin: '0 0 4px 0', color: customTextMain }}>
            {wo.product_name}
          </h4>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
            <div style={{ color: customTextSub }}>
              Qty: <strong style={{ color: customTextMain, fontSize: '13px' }}>{wo.qty} pcs</strong>
            </div>
            {wo.isMachinery && (wo.totalComponentsCount ?? 0) > 0 ? (
              <div style={{ color: customTextSub }}>
                Components: <strong style={{ color: isCompleted ? '#52c41a' : '#1890ff', fontSize: '13px' }}>
                  {wo.completedComponentsCount || 0} / {wo.totalComponentsCount} done
                </strong>
              </div>
            ) : !wo.isMachinery ? (
              <div style={{ color: customTextSub }}>
                Complete: <strong style={{ color: '#52c41a', fontSize: '13px' }}>{wo.total_complete || 0} pcs</strong>
              </div>
            ) : null}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', marginTop: '6px' }}>
            <div style={{ color: customTextSub }}>
              Assigned To: <strong style={{ color: customTextMain }}>{wo.employee_fname ? `${wo.employee_fname} ${wo.employee_lname || ''}`.trim() : 'Unassigned'}</strong>
            </div>
          </div>
        </div>

        {/* Section 3: Production Progress Bar */}
        <div style={{ width: '100%', margin: '12px 0 4px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', fontWeight: '700', color: customTextSub, textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '6px' }}>
            <span>Status: <strong style={{ color: cardAccentColor, textTransform: 'none' }}>{cardStageLabel}</strong></span>
            <span>{Math.round(progressPercent)}%</span>
          </div>

          <Progress
            percent={Math.min(100, Math.round(progressPercent))}
            strokeColor={{
              '0%': '#1890ff',
              '100%': cardAccentColor,
            }}
            trailColor={darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}
            showInfo={false}
            size="small"
            style={{ margin: 0 }}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '32px 24px', minHeight: '90vh', backgroundColor: customLayoutBg, transition: 'background-color 0.3s ease' }}>

      {/* HEADER PANEL & FILTERS */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        marginBottom: '36px',
        borderBottom: `2px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
        paddingBottom: '28px'
      }}>
        <div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            margin: '0 0 6px 0',
            letterSpacing: '-0.02em',
            color: customTextMain,
          }}>
            Track Work Orders
          </h1>
          <p style={{ fontSize: '15px', margin: 0, color: customTextSub }}>
            Monitor active production stages, filter by active department, assigned manager, or specific work order codes.
          </p>
        </div>

        {/* FILTERS CONSOLE BAR */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>

          {/* Department Filter */}
          <Select
            size="large"
            placeholder="Filter by Active Department"
            allowClear
            value={selectedDept}
            onChange={(val) => setSelectedDept(val)}
            style={{ width: '220px' }}
          >
            {DEPARTMENTS.map(d => (
              <Select.Option key={d.key} value={d.key}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: d.color }} />
                  {d.label}
                </span>
              </Select.Option>
            ))}
          </Select>

          {/* User Filter */}
          <Select
            size="large"
            placeholder="Filter by Creator/User"
            allowClear
            value={selectedUser}
            onChange={(val) => setSelectedUser(val)}
            style={{ width: '220px' }}
          >
            {users.map(u => (
              <Select.Option key={u.user_id} value={u.user_id}>
                {u.user_name} ({u.user_email})
              </Select.Option>
            ))}
          </Select>

          {/* PI Filter */}
          <Select
            size="large"
            placeholder="Filter by PI #"
            allowClear
            showSearch
            value={selectedPI}
            onChange={(val) => handlePIChange(val || null)}
            style={{ width: '180px' }}
          >
            {piOptions.map((piNo) => (
              <Select.Option key={piNo} value={piNo}>
                PI #{piNo}
              </Select.Option>
            ))}
          </Select>

          {/* Query Wise Search */}
          <Input
            size="large"
            placeholder="Search by WO #, client, product..."
            prefix={<Icons.SearchOutlined style={{ color: customTextSub }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            style={{
              width: '320px',
              borderRadius: '6px',
              border: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)'}`,
              background: customCardBg,
              color: customTextMain,
              fontSize: '13px'
            }}
          />

          {/* Download Work Order Button */}
          <Button
            size="large"
            type="primary"
            icon={<Icons.DownloadOutlined />}
            onClick={() => {
              setSelectedWOIds([]);
              setDownloadModalVisible(true);
            }}
            style={{
              borderRadius: '6px',
              fontWeight: 600
            }}
          >
            Download Work Order
          </Button>

          {/* Clear Filters Button */}
          <Button
            size="large"
            icon={<Icons.ClearOutlined />}
            onClick={handleClearFilters}
            disabled={!selectedDept && selectedUser === null && !selectedPI && !searchQuery}
            style={{
              borderRadius: '6px',
              fontWeight: 600
            }}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* CORE CARDS CONTENT */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh', width: '100%' }}>
          <Spin size="large" />
        </div>
      ) : filteredWorkOrders.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', width: '100%' }}>
          {DEPARTMENTS.map(dept => {
            const list = groupedWorkOrders[dept.key] || [];
            if (list.length === 0) return null;

            return (
              <div key={dept.key} style={{
                padding: '24px',
                background: darkMode ? 'rgba(30, 41, 59, 0.25)' : 'rgba(255, 255, 255, 0.45)',
                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
                borderRadius: '16px',
              }}>
                {/* Department Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  borderBottom: `2px solid ${dept.color}25`,
                  paddingBottom: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: dept.color,
                      boxShadow: `0 0 8px ${dept.color}`
                    }} />
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '800',
                      margin: 0,
                      color: customTextMain,
                      letterSpacing: '-0.01em'
                    }}>
                      {dept.label} Department
                    </h3>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      background: `${dept.color}15`,
                      color: dept.color,
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {list.length} {list.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </div>

                {/* Cards Grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: '24px',
                    width: '100%',
                  }}
                >
                  {list.map((wo, index) => {
                    const isCompleted = wo.isCompleted ?? (Number(wo.total_complete || 0) >= Number(wo.qty));
                    return renderWorkOrderCard(wo, index, isCompleted);
                  })}
                </div>
              </div>
            );
          })}

          {/* Unassigned section at the end */}
          {(() => {
            const list = groupedWorkOrders['unassigned'] || [];
            if (list.length === 0) return null;

            return (
              <div key="unassigned" style={{
                padding: '24px',
                background: darkMode ? 'rgba(30, 41, 59, 0.25)' : 'rgba(255, 255, 255, 0.45)',
                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
                borderRadius: '16px',
              }}>
                {/* Department Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  borderBottom: `2px solid #8c8c8c25`,
                  paddingBottom: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: '#8c8c8c',
                      boxShadow: `0 0 8px #8c8c8c`
                    }} />
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '800',
                      margin: 0,
                      color: customTextMain,
                      letterSpacing: '-0.01em'
                    }}>
                      Unassigned Department
                    </h3>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      background: `#8c8c8c15`,
                      color: '#8c8c8c',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {list.length} {list.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </div>

                {/* Cards Grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: '24px',
                    width: '100%',
                  }}
                >
                  {list.map((wo, index) => {
                    const isCompleted = wo.isCompleted ?? (Number(wo.total_complete || 0) >= Number(wo.qty));
                    return renderWorkOrderCard(wo, index, isCompleted);
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        /* EMPTY STATE */
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh', width: '100%' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: customTextSub, fontSize: '15px' }}>
                {flatWorkOrders.length > 0
                  ? "No work orders match the selected filters."
                  : "No production items found in performa invoices."}
              </span>
            }
          />
        </div>
      )}
      {/* 1. Daily Completion Details Modal */}
      <Modal
        title={
          <span style={{ fontSize: '18px', fontWeight: '800', color: darkMode ? '#f8fafc' : '#0f172a' }}>
            Production Entries - WO-{selectedWO?.wo_id}
          </span>
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        width={850}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>
        ]}
        bodyStyle={{ padding: '20px 24px' }}
      >
        {selectedWO && (
          <div>
            {/* Upper details block */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '24px',
              background: darkMode ? '#1e293b' : '#f8fafc',
              border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
              borderRadius: '8px',
              padding: '16px'
            }}>
              <div>
                <p style={{ margin: '0 0 8px 0', color: customTextSub }}>
                  W/O ID: <strong style={{ color: customTextMain }}>WO-{selectedWO.wo_id}</strong>
                </p>
                <p style={{ margin: '0 0 8px 0', color: customTextSub }}>
                  PI Order No: <strong style={{ color: customTextMain }}>#{selectedWO.pi_order_no}</strong>
                </p>
                <p style={{ margin: '0 0 8px 0', color: customTextSub }}>
                  Work Order Issue Date: <strong style={{ color: customTextMain }}>{dayjs(selectedWO.pi_date).format('DD-MMM-YYYY')}</strong>
                </p>
              </div>
              <div>
                <p style={{ margin: '0 0 8px 0', color: customTextSub }}>
                  Deadline: <strong style={{ color: customTextMain }}>
                    {selectedWO.wo_due_date
                      ? dayjs(selectedWO.wo_due_date).format('DD-MMM-YYYY')
                      : dayjs(selectedWO.pi_date).add(selectedWO.pi_lead_time, 'day').format('DD-MMM-YYYY')}
                  </strong>
                </p>
                <p style={{ margin: '0 0 8px 0', color: customTextSub }}>
                  Status: <span className="track-badge" style={{
                    '--accent-color': selectedWO.borderColor,
                    '--badge-bg': selectedWO.badgeBg
                  } as React.CSSProperties}>{selectedWO.statusText}</span>
                </p>
              </div>
            </div>

            {/* Component Progress Dashboard (For Machinery) */}
            {showComponentDropdown && bomComponents.length > 0 && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                background: darkMode ? 'rgba(30, 41, 59, 0.4)' : '#f8fafc',
                border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)'}`,
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: customTextMain }}>
                  Component Completion Progress
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                  {bomComponents.map((c: any) => {
                    const compName = c.component_name;
                    const compCompleted = dailyDetails
                      .filter(d => {
                        if (Number(d.wod_qty) <= 0) return false;
                        const match = String(d.wod_remarks || '').match(/^\[(.*?)\]/);
                        return match && match[1] === compName;
                      })
                      .reduce((sum, d) => sum + Number(d.wod_qty), 0);

                    const percent = Math.min(Math.round((compCompleted / selectedWO.qty) * 100), 100);

                    return (
                      <div key={compName} style={{
                        padding: '10px 12px',
                        background: darkMode ? '#0f172a' : '#ffffff',
                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                        borderRadius: '6px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600', fontSize: '12px', color: customTextMain, marginBottom: '6px' }}>
                          <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', marginRight: '8px' }} title={compName}>
                            {compName}
                          </span>
                          <span style={{ flexShrink: 0 }}>{selectedWO.qty}/{compCompleted}</span>
                        </div>
                        <Progress
                          percent={percent}
                          size="small"
                          strokeColor={percent >= 100 ? "#52c41a" : "#1890ff"}
                          showInfo={false}
                          style={{ margin: 0 }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Table Upper Part Info */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`
            }}>
              <div style={{ fontSize: '15px', fontWeight: '700' }}>
                <span style={{ color: '#52c41a' }}>{selectedWO.product_name} - Total Pcs {selectedWO.qty}</span>
                {!selectedWO.isMachinery ? (
                  <span style={{ color: '#ff4d4f' }}> ({dailyDetails.filter(d => Number(d.wod_qty) > 0).reduce((sum, d) => sum + Number(d.wod_qty), 0)} completed pcs)</span>
                ) : (
                  <span style={{ color: selectedWO.isCompleted ? '#52c41a' : '#1890ff' }}>
                    {' '}({selectedWO.completedComponentsCount || 0}/{selectedWO.totalComponentsCount || 0} components done - {Math.round(selectedWO.progressPercent || 0)}%)
                  </span>
                )}
              </div>
              <Button
                type="primary"
                icon={<Icons.PlusOutlined />}
                onClick={handleOpenAdd}
                style={{ borderRadius: '6px' }}
              >
                Log Entry
              </Button>
            </div>

            {/* Table Column Details */}
            <Table
              dataSource={dailyDetails}
              rowKey="wod_id"
              loading={loadingDetails}
              pagination={false}
              size="small"
              bordered
              style={{ background: customCardBg }}
              columns={[
                {
                  title: 'Sr',
                  key: 'sr',
                  width: 60,
                  align: 'center',
                  render: (_, __, index) => index + 1
                },
                {
                  title: 'Date and time',
                  dataIndex: 'wod_created_on',
                  key: 'wod_created_on',
                  render: (val) => dayjs(val).format('DD-MMM-YYYY hh:mm A')
                },
                {
                  title: 'Component',
                  key: 'wod_component',
                  render: (_, record) => {
                    const match = String(record.wod_remarks || '').match(/^\[(.*?)\]/);
                    return match ? match[1] : '-';
                  }
                },
                {
                  title: 'No of pcs',
                  dataIndex: 'wod_qty',
                  key: 'wod_qty',
                  render: (qty) => {
                    const isPositive = qty > 0;
                    return (
                      <span style={{ fontWeight: '700', color: isPositive ? '#52c41a' : '#ff4d4f' }}>
                        {isPositive ? `+${qty}` : qty} pcs
                      </span>
                    );
                  }
                },
                {
                  title: 'Approve by (created By)',
                  dataIndex: 'approved_by_name',
                  key: 'approved_by_name',
                  render: (val) => val || 'System'
                },
                {
                  title: 'Remarks',
                  dataIndex: 'wod_remarks',
                  key: 'wod_remarks',
                  render: (val) => {
                    const match = String(val || '').match(/^\[(.*?)\](?: (.*))?$/);
                    return match ? (match[2] || '-') : (val || '-');
                  }
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 120,
                  align: 'center',
                  render: (_, record) => (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <Button
                        size="small"
                        icon={<Icons.EditOutlined />}
                        onClick={() => handleOpenEdit(record)}
                      />
                      <Popconfirm
                        title="Delete this daily log entry?"
                        onConfirm={() => handleDeleteDetail(record.wod_id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          size="small"
                          danger
                          icon={<Icons.DeleteOutlined />}
                        />
                      </Popconfirm>
                    </div>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>

      {/* 2. Add / Edit Production Entry Modal */}
      <Modal
        title={
          <span style={{ fontWeight: '700', color: darkMode ? '#f8fafc' : '#0f172a' }}>
            {editingDetail ? 'Edit Production Entry' : 'Log Production Progress'}
          </span>
        }
        open={formModalVisible}
        onCancel={() => setFormModalVisible(false)}
        onOk={() => form.submit()}
        okText="Save"
        cancelText="Cancel"
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveDetail}
          style={{ marginTop: '16px' }}
        >
          {showComponentDropdown && (
            <Form.Item
              name="wod_component"
              label="Select Component"
              rules={[{ required: true, message: 'Please select a component' }]}
            >
              <Select placeholder="Select component from BOM">
                {bomComponents.map((c: any) => (
                  <Select.Option key={c.component_key || c.component_name} value={c.component_name}>
                    {c.component_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="wod_qty"
            label="Number of Pieces"
            rules={[
              { required: true, message: 'Please enter quantity' },
              {
                validator: (_, value) => {
                  if (value === 0) {
                    return Promise.reject(new Error('Quantity cannot be zero'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
            extra="Positive value = Completed Pieces. Negative value = Rejected Pieces."
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="e.g. 15 or -3"
            />
          </Form.Item>

          <Form.Item
            name="wod_created_on"
            label="Date & Time"
            rules={[{ required: true, message: 'Please select date & time' }]}
            extra={!editingDetail ? "Automatically set to current date & time on entry." : undefined}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
              disabled={!editingDetail}
            />
          </Form.Item>

          <Form.Item
            name="wod_remarks"
            label="Remarks / Notes"
          >
            <Input.TextArea rows={3} placeholder="Optional details..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* 3. Download Select Work Orders Modal */}
      <Modal
        title={
          <span style={{ fontSize: '18px', fontWeight: '800', color: darkMode ? '#f8fafc' : '#0f172a' }}>
            Download Work Order PDFs
          </span>
        }
        open={downloadModalVisible}
        onCancel={() => setDownloadModalVisible(false)}
        width={750}
        footer={[
          <Button key="cancel" onClick={() => setDownloadModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<Icons.DownloadOutlined />}
            loading={downloadingPdf}
            onClick={handleDownloadSelectedPDF}
            disabled={selectedWOIds.length === 0}
          >
            Download Selected ({selectedWOIds.length})
          </Button>
        ]}
      >
        <div style={{ margin: '8px 0 16px 0' }}>
          Select the work orders you want to download. A single consolidated PDF will be generated containing one page per selected work order.
        </div>

        <Table
          dataSource={flatWorkOrders}
          rowKey="wo_id"
          pagination={{ pageSize: 5 }}
          size="small"
          rowSelection={{
            selectedRowKeys: selectedWOIds,
            onChange: (keys) => setSelectedWOIds(keys as number[])
          }}
          columns={[
            {
              title: 'WO ID',
              dataIndex: 'wo_id',
              key: 'wo_id',
              render: (id) => `WO-${id}`
            },
            {
              title: 'PI Order #',
              dataIndex: 'pi_order_no',
              key: 'pi_order_no'
            },
            {
              title: 'Product',
              dataIndex: 'product_name',
              key: 'product_name'
            },
            {
              title: 'Target Qty',
              dataIndex: 'qty',
              key: 'qty',
              render: (qty) => `${qty} pcs`
            },
            {
              title: 'Complete',
              render: (_, record: FlatWorkOrder) => {
                if (record.isMachinery && (record.totalComponentsCount ?? 0) > 0) {
                  return `${record.completedComponentsCount || 0}/${record.totalComponentsCount} comps (${Math.round(record.progressPercent || 0)}%)`;
                }
                return `${record.total_complete || 0} pcs`;
              }
            }
          ]}
        />
      </Modal>

    </div>
  );
};

export default TrackWorkOrder;
